import React, { useState } from "react";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
// mismas tarjetas, formulario y títulos que Catálogos y Usuarios
import style from "../../scss/Catalogos.module.scss";
import estiloAgenda from "../../scss/Agenda.module.scss";
import useAgenda, { notificarCambioAgenda } from "../../hooks/useAgenda";
import CompromisoAgendaComponent from "./component/CompromisoAgendaComponent";
import {
  crearCompromisoAgenda,
  editarCompromisoAgenda,
  editarEstadoAgenda,
  eliminarCompromisoAgenda,
} from "../../Api/ApiAgenda";
import { FILTROS_AGENDA, compromisosPorAvisar, esDeVotante, fechaHoyTexto, horaParaInput } from "../../utils/agenda";
import { editarCompromisoCumplido } from "../../Api/ApiMetodo";
import { esAdmin } from "../../utils/sesion";
import { MAX_COMPROMISO } from "../../constants/compromiso";
import { obtenerMensajeError } from "../../utils/mensajeError";
import SelectComponent from "../../components/SelectComponent";
import useOpcionesElectores from "../../hooks/useOpcionesElectores";
import useLideres from "../../hooks/useLideres";

const FORMULARIO_VACIO = { descripcion: "", fecha: "", hora: "", electorId: null, lider: null, avisarCorreo: true };

const notificarExito = (mensaje) =>
  Swal.fire({ icon: "success", text: mensaje, timer: 1500, showConfirmButton: false });

const notificarError = (error) => Swal.fire({ icon: "error", text: obtenerMensajeError(error) });

const AgendaPages = () => {
  const { compromisos, cargando } = useAgenda();
  const { opcionesVotante } = useOpcionesElectores();
  const { opcionesLider } = useLideres();
  const admin = esAdmin();
  // recarga la página y la campana del encabezado
  const recargar = notificarCambioAgenda;
  const [formulario, setFormulario] = useState(FORMULARIO_VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [filtro, setFiltro] = useState(FILTROS_AGENDA[0].valor);

  const filtroActivo = FILTROS_AGENDA.find((f) => f.valor === filtro);
  const visibles = compromisos.filter(filtroActivo.cumple);
  const totalPorAvisar = compromisosPorAvisar(compromisos).length;

  // mensaje en lugar de la lista mientras carga o si el filtro no deja compromisos
  const obtenerMensajeVacio = () => {
    if (cargando && compromisos.length === 0) {
      return (
        <>
          <span className="spinner-border spinner-border-sm" aria-hidden="true" /> Cargando agenda…
        </>
      );
    }
    if (visibles.length === 0) {
      return (
        <>
          <Icon icon="mdi:calendar-blank-outline" aria-hidden="true" />
          {filtro === "cumplidos" ? "Aún no has cumplido compromisos." : "No tienes compromisos en esta lista."}
        </>
      );
    }
    return null;
  };
  const mensajeVacio = obtenerMensajeVacio();

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormulario({ ...formulario, [name]: type === "checkbox" ? checked : value });
  };

  // sin fecha no hay hora (la hora sola no sirve para avisar)
  const cambiarFecha = (e) =>
    setFormulario({ ...formulario, fecha: e.target.value, hora: e.target.value ? formulario.hora : "" });

  // SelectComponent entrega solo el value (null al limpiar)
  const cambiarSelect = (campo) => (valor) => setFormulario({ ...formulario, [campo]: valor });

  const limpiarFormulario = () => {
    setFormulario(FORMULARIO_VACIO);
    setEditandoId(null);
  };

  const iniciarEdicion = (compromiso) => {
    setEditandoId(compromiso.id);
    setFormulario({
      descripcion: compromiso.descripcion,
      fecha: compromiso.fecha ?? "",
      hora: horaParaInput(compromiso.hora),
      electorId: compromiso.Elector ? compromiso.electorId : null,
      lider: compromiso.lider,
      avisarCorreo: compromiso.avisarCorreo !== false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const datos = { ...formulario, descripcion: formulario.descripcion.trim() };
    if (!datos.descripcion) return;

    setGuardando(true);
    try {
      const respuesta = editandoId
        ? await editarCompromisoAgenda(editandoId, datos)
        : await crearCompromisoAgenda(datos);
      notificarExito(respuesta.msg);
      limpiarFormulario();
      recargar();
    } catch (error) {
      notificarError(error);
    } finally {
      setGuardando(false);
    }
  };

  // el compromiso con un votante se marca en el elector (mismo dato del listado)
  const cambiarEstado = async (compromiso) => {
    try {
      const respuesta = esDeVotante(compromiso)
        ? await editarCompromisoCumplido(compromiso.electorId, !compromiso.cumplido)
        : await editarEstadoAgenda(compromiso.id, !compromiso.cumplido);
      notificarExito(respuesta.msg);
      recargar();
    } catch (error) {
      notificarError(error);
    }
  };

  const confirmarEliminar = (compromiso) => {
    Swal.fire({
      title: "¿Eliminar este compromiso?",
      text: compromiso.descripcion,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const respuesta = await eliminarCompromisoAgenda(compromiso.id);
        notificarExito(respuesta.msg);
        if (editandoId === compromiso.id) limpiarFormulario();
        recargar();
      } catch (error) {
        notificarError(error);
      }
    });
  };

  return (
    <main className={`container ${style.catalogos}`}>
      <h1 className={style.encabezado}>Agenda</h1>
      <p className={style.descripcion}>
        Tus compromisos personales y los compromisos con votantes que tienen fecha. Te avisamos cuando un
        compromiso es hoy, mañana o en los próximos días, y cuando se venció sin cumplirse.
      </p>

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <section className={style.tarjeta}>
            <h2 className={style.titulo}>{editandoId ? "Editar compromiso" : "Nuevo compromiso"}</h2>

            <form onSubmit={handleSubmit} className="row g-2">
              <div className="col-12">
                <label htmlFor="agenda-descripcion" className={style.etiqueta}>
                  Descripción
                </label>
                <textarea
                  id="agenda-descripcion"
                  name="descripcion"
                  className="form-control"
                  rows={4}
                  maxLength={MAX_COMPROMISO}
                  value={formulario.descripcion}
                  onChange={handleChange}
                  placeholder="Ej.: Reunión con los líderes del barrio Naranjal"
                  required
                />
              </div>
              <div className="col-7">
                <label htmlFor="agenda-fecha" className={style.etiqueta}>
                  Fecha <span className={estiloAgenda.opcional}>(opcional)</span>
                </label>
                <input
                  id="agenda-fecha"
                  name="fecha"
                  type="date"
                  className="form-control"
                  // al crear se sugiere desde hoy; al editar se permite dejar una fecha pasada
                  min={editandoId ? undefined : fechaHoyTexto()}
                  value={formulario.fecha}
                  onChange={cambiarFecha}
                />
              </div>
              <div className="col-5">
                <label htmlFor="agenda-hora" className={style.etiqueta}>
                  Hora <span className={estiloAgenda.opcional}>(opcional)</span>
                </label>
                <input
                  id="agenda-hora"
                  name="hora"
                  type="time"
                  className="form-control"
                  value={formulario.hora}
                  onChange={handleChange}
                  disabled={!formulario.fecha}
                  title={formulario.fecha ? "" : "Primero elige la fecha"}
                />
              </div>

              <fieldset className={`col-12 ${estiloAgenda.grupo}`}>
                <legend className={estiloAgenda.grupoTitulo}>
                  Relacionado con <span className={estiloAgenda.opcional}>(opcional)</span>
                </legend>
                <div className="row g-2">
                  <div className="col-12">
                    <SelectComponent
                      id="agenda-votante"
                      etiqueta="Votante"
                      claseEtiqueta={style.etiqueta}
                      opciones={opcionesVotante}
                      valor={formulario.electorId}
                      onCambiar={cambiarSelect("electorId")}
                      placeholder="Buscar por nombre o cédula"
                    />
                  </div>
                  <div className="col-12">
                    <SelectComponent
                      id="agenda-lider"
                      etiqueta="Líder"
                      claseEtiqueta={style.etiqueta}
                      opciones={opcionesLider}
                      valor={formulario.lider}
                      onCambiar={cambiarSelect("lider")}
                      placeholder="Seleccionar líder"
                    />
                  </div>
                </div>
              </fieldset>

              <div className="col-12">
                <div className={`form-check form-switch ${estiloAgenda.interruptor}`}>
                  <input
                    id="agenda-avisar-correo"
                    name="avisarCorreo"
                    type="checkbox"
                    role="switch"
                    className="form-check-input"
                    checked={formulario.avisarCorreo}
                    onChange={handleChange}
                    disabled={!formulario.fecha}
                  />
                  <label htmlFor="agenda-avisar-correo" className="form-check-label">
                    Avisarme por correo el día anterior
                  </label>
                </div>
                {!formulario.fecha && (
                  <p className={estiloAgenda.ayuda}>El aviso por correo necesita la fecha del compromiso.</p>
                )}
              </div>

              <div className={`col-12 ${style.botones}`}>
                <button type="submit" className={`btn btn-primary ${style.boton}`} disabled={guardando}>
                  {editandoId ? "Guardar cambios" : "Agregar a la agenda"}
                </button>
                {editandoId && (
                  <button type="button" className={`btn btn-outline-secondary ${style.boton}`} onClick={limpiarFormulario}>
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </section>
        </div>

        <div className="col-12 col-lg-8">
          <section className={style.tarjeta} aria-labelledby="titulo-agenda">
            <div className={estiloAgenda.cabecera}>
              <h2 id="titulo-agenda" className={style.titulo}>
                Mis compromisos <span className={style.contador}>{compromisos.length}</span>
              </h2>
              {totalPorAvisar > 0 && (
                <span className={estiloAgenda.alertaCabecera}>
                  <Icon icon="mdi:bell-ring-outline" aria-hidden="true" />
                  {totalPorAvisar} por atender
                </span>
              )}
            </div>

            <div className={estiloAgenda.filtros} role="tablist" aria-label="Filtrar compromisos">
              {FILTROS_AGENDA.map((opcion) => (
                <button
                  key={opcion.valor}
                  type="button"
                  role="tab"
                  aria-selected={filtro === opcion.valor}
                  className={`${estiloAgenda.filtro} ${filtro === opcion.valor ? estiloAgenda.filtroActivo : ""}`}
                  onClick={() => setFiltro(opcion.valor)}
                >
                  {opcion.etiqueta}
                  <span className={estiloAgenda.filtroContador}>{compromisos.filter(opcion.cumple).length}</span>
                </button>
              ))}
            </div>

            {mensajeVacio ? (
              <p className={estiloAgenda.vacio}>{mensajeVacio}</p>
            ) : (
              <ul className={estiloAgenda.lista}>
                {visibles.map((compromiso) => (
                  <CompromisoAgendaComponent
                    key={compromiso.id}
                    compromiso={compromiso}
                    editando={editandoId === compromiso.id}
                    // los compromisos con votantes solo los marca el admin (igual que en la consulta)
                    puedeCambiarEstado={!esDeVotante(compromiso) || admin}
                    onCambiarEstado={cambiarEstado}
                    onEditar={iniciarEdicion}
                    onEliminar={confirmarEliminar}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default AgendaPages;
